import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { api, unwrapPage } from '@/api/client';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaLarga } from '@/lib/formato';

import { useOpcionesLicencia } from './queries';

type Fila = components['schemas']['MembershipUser'];
const CLAVES = ['membership'] as const;

function PorLicencia() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const opciones = useOpcionesLicencia();
  const id = filtros.membership;
  const q = useQuery({
    queryKey: ['alumnos', 'por-licencia', id, filtros.page],
    queryFn: () =>
      unwrapPage<Fila[]>(
        api.GET('/api/v1/admin/memberships/{id}/users', {
          params: { path: { id }, query: { page: filtros.page, pageSize: 50 } },
        }),
      ),
    enabled: id !== '',
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <Encabezado
        titulo="Alumnos por licencia"
        descripcion="Quién tiene hoy una licencia vigente."
      />
      <select
        className="input-vidrio mb-4 max-w-md"
        value={id}
        onChange={(e) => cambiar({ membership: e.target.value })}
        aria-label="Licencia"
      >
        <option value="">Elige una licencia…</option>
        {opciones.data?.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
            {o.isActive ? '' : ' (inactiva)'}
          </option>
        ))}
      </select>
      {id === '' ? (
        <Vacio titulo="Elige una licencia para ver a sus alumnos" />
      ) : q.isPending ? (
        <Cargando />
      ) : q.isError ? (
        <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />
      ) : q.data.data.length === 0 ? (
        <Vacio titulo="Nadie tiene esta licencia vigente" />
      ) : (
        <>
          <div className="panel-vidrio overflow-x-auto p-0">
            <table className="tabla-cs w-full text-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Vigencia</th>
                </tr>
              </thead>
              <tbody>
                {q.data.data.map((a) => (
                  <tr key={a.id}>
                    <td className="tabular">{a.id}</td>
                    <td>
                      <Link to={`/alumnos/${a.id}?tab=licencias`} className="font-bold">
                        {a.name}
                      </Link>
                    </td>
                    <td>{a.email}</td>
                    <td className="whitespace-nowrap">
                      {fechaLarga(a.startsOn)} – {fechaLarga(a.endsOn)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginador
            page={q.data.page}
            pageSize={q.data.pageSize}
            total={q.data.total}
            onPage={(page) => cambiar({ page })}
          />
        </>
      )}
    </>
  );
}

export function PorLicenciaPage() {
  return (
    <PermissionGate seccion="alumnos">
      <PorLicencia />
    </PermissionGate>
  );
}
